/**
 * Mobile Quick Report Page
 * Ultra-fast mobile-optimized issue reporting for field staff.
 * Goal: Create job in under 10 seconds.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Zap, Flame, Thermometer, Droplet, Waves, Bell, Droplets,
  Building, Wrench, AlertTriangle, HelpCircle, Plus, 
  Loader2, CheckCircle2, Camera, X, MapPin, Mic, MicOff,
  ChevronRight, Home
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Quick issue buttons
const QUICK_ISSUES = [
  { value: 'electrical_fault', label: 'Electrical', icon: Zap, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'gas_issue', label: 'Gas', icon: Flame, color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'plumbing_leak', label: 'Plumbing', icon: Droplet, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'boiler_issue', label: 'Boiler', icon: Thermometer, color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'fire_alarm_issue', label: 'Fire Alarm', icon: Bell, color: 'bg-rose-100 text-rose-700 border-rose-200' },
  { value: 'general_repair', label: 'Repair', icon: Wrench, color: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'damage_report', label: 'Damage', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'unknown_other', label: 'Other', icon: HelpCircle, color: 'bg-gray-100 text-gray-700 border-gray-200' }
];

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'URGENT', color: 'bg-red-600 text-white' },
  { value: 'high', label: 'High', color: 'bg-orange-500 text-white' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-500 text-slate-900' },
  { value: 'low', label: 'Low', color: 'bg-green-500 text-white' }
];

export default function MobileQuickReportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  // Pre-fill from URL params
  const prefilledParkId = searchParams.get('park_id');
  const prefilledParkName = searchParams.get('park_name');
  
  // Multi-step form state
  const [step, setStep] = useState(1); // 1: Type, 2: Location, 3: Details, 4: Confirm
  const [formData, setFormData] = useState({
    issue_type: '',
    priority: 'medium',
    park_id: prefilledParkId || '',
    park_name: prefilledParkName || '',
    unit_name: '',
    description: '',
    contact_name: '',
    contact_phone: '',
    source_type: 'mobile_quick_report',
    apply_auto_assignment: true
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdJob, setCreatedJob] = useState(null);
  const [parks, setParks] = useState([]);
  const [parkSearch, setParkSearch] = useState(prefilledParkName || '');
  const [photos, setPhotos] = useState([]);
  const [showParkList, setShowParkList] = useState(false);
  
  // Fetch parks on mount
  useEffect(() => {
    fetchParks();
  }, []);

  const fetchParks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/jobs/intake/options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setParks(response.data.parks || []);
      }
    } catch (err) {
      console.error('Failed to fetch parks:', err);
    }
  };

  const selectIssue = (issue) => {
    setFormData(prev => ({ ...prev, issue_type: issue.value }));
    // Auto-advance to next step
    setTimeout(() => setStep(2), 150);
  };

  const selectPriority = (priority) => {
    setFormData(prev => ({ ...prev, priority: priority.value }));
  };

  const selectPark = (park) => {
    setFormData(prev => ({
      ...prev,
      park_id: park.id,
      park_name: park.name
    }));
    setParkSearch(park.name);
    setShowParkList(false);
  };

  const handlePhotoCapture = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  const handleSubmit = async () => {
    if (!formData.park_id || !formData.issue_type) {
      toast.error('Please select site and issue type');
      return;
    }
    
    setCreating(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Build description if empty
      const finalDescription = formData.description || 
        `${QUICK_ISSUES.find(i => i.value === formData.issue_type)?.label || 'Issue'} reported via mobile`;
      
      const response = await axios.post(`${API}/api/jobs/intake/create`, {
        ...formData,
        description: finalDescription
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.ok) {
        setCreatedJob(response.data);
        setSubmitted(true);
        toast.success('Job created!');
        
        // Upload photos if any
        if (photos.length > 0) {
          for (const photo of photos) {
            const photoForm = new FormData();
            photoForm.append('file', photo.file);
            photoForm.append('file_category', 'before');
            
            try {
              await axios.post(
                `${API}/api/fm/jobs/${response.data.job_id}/upload-completion`,
                photoForm,
                { headers: { Authorization: `Bearer ${token}` } }
              );
            } catch (err) {
              console.error('Photo upload failed:', err);
            }
          }
        }
      } else if (response.data.require_duplicate_override) {
        // Auto-override for mobile speed
        const retryResponse = await axios.post(`${API}/api/jobs/intake/create`, {
          ...formData,
          description: finalDescription,
          override_duplicate_warning: true
        }, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (retryResponse.data.ok) {
          setCreatedJob(retryResponse.data);
          setSubmitted(true);
          toast.success('Job created!');
        } else {
          toast.error(retryResponse.data.error || 'Failed to create');
        }
      } else {
        toast.error(response.data.error || 'Failed to create');
      }
    } catch (err) {
      console.error('Create error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create job');
    } finally {
      setCreating(false);
    }
  };

  // Filter parks
  const filteredParks = parkSearch
    ? parks.filter(p => 
        p.name?.toLowerCase().includes(parkSearch.toLowerCase()) ||
        p.postcode?.toLowerCase().includes(parkSearch.toLowerCase())
      )
    : parks;

  // Success Screen
  if (submitted && createdJob) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1F7A78] to-[#005a5c] flex items-center justify-center p-4" data-testid="mobile-quick-success">
        <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Job Created!</h1>
          <p className="text-slate-500 mb-6">Reference: <span className="font-mono font-bold">{createdJob.job_reference}</span></p>
          
          <div className="space-y-3">
            <Button 
              onClick={() => navigate(`/fm/jobs/${createdJob.job_id}`)}
              className="w-full bg-[#1F7A78] hover:bg-[#005f5f] h-12"
            >
              View Job
            </Button>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setCreatedJob(null);
                setStep(1);
                setFormData({
                  issue_type: '',
                  priority: 'medium',
                  park_id: prefilledParkId || '',
                  park_name: prefilledParkName || '',
                  unit_name: '',
                  description: '',
                  contact_name: '',
                  contact_phone: '',
                  source_type: 'mobile_quick_report',
                  apply_auto_assignment: true
                });
                setPhotos([]);
                setParkSearch(prefilledParkName || '');
              }}
              variant="outline"
              className="w-full h-12"
            >
              Report Another
            </Button>
            <Button 
              onClick={() => navigate('/fm')}
              variant="ghost"
              className="w-full"
            >
              <Home className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="mobile-quick-report-page">
      {/* Header */}
      <div className="bg-[#1F7A78] text-white px-4 py-4 safe-top">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">Quick Report</h1>
            <p className="text-xs text-white/70">Step {step} of 3</p>
          </div>
          <Badge className="bg-white/20 text-white">
            {QUICK_ISSUES.find(i => i.value === formData.issue_type)?.label || 'Select Issue'}
          </Badge>
        </div>
        
        {/* Progress bar */}
        <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white transition-all duration-300"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>
      </div>
      
      <div className="p-4 pb-24">
        
        {/* Step 1: Issue Type */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-1-issue">
            <h2 className="text-lg font-semibold text-slate-800">What's the issue?</h2>
            <div className="grid grid-cols-2 gap-3">
              {QUICK_ISSUES.map(issue => {
                const Icon = issue.icon;
                const isSelected = formData.issue_type === issue.value;
                
                return (
                  <button
                    key={issue.value}
                    onClick={() => selectIssue(issue)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${
                      isSelected 
                        ? 'border-[#1F7A78] bg-[#1F7A78]/10 scale-[1.02]' 
                        : `${issue.color} border-2`
                    }`}
                    data-testid={`quick-issue-${issue.value}`}
                  >
                    <Icon className={`h-8 w-8 ${isSelected ? 'text-[#1F7A78]' : ''}`} />
                    <span className={`text-sm font-medium ${isSelected ? 'text-[#1F7A78]' : ''}`}>
                      {issue.label}
                    </span>
                  </button>
                );
              })}
            </div>
            
            {/* Priority Selection */}
            <div className="mt-6">
              <h3 className="text-sm font-medium text-slate-600 mb-2">Priority</h3>
              <div className="flex gap-2">
                {PRIORITY_OPTIONS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => selectPriority(p)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      formData.priority === p.value
                        ? p.color
                        : 'bg-slate-200 text-slate-600'
                    }`}
                    data-testid={`quick-priority-${p.value}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Step 2: Location */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-2-location">
            <h2 className="text-lg font-semibold text-slate-800">Where is it?</h2>
            
            {/* Park Search */}
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search parks..."
                value={parkSearch}
                onChange={(e) => {
                  setParkSearch(e.target.value);
                  setShowParkList(true);
                }}
                onFocus={() => setShowParkList(true)}
                className="pl-10 h-12 text-base rounded-xl"
                data-testid="quick-park-search"
              />
            </div>
            
            {/* Park List */}
            {showParkList && (
              <div className="bg-white rounded-xl border border-slate-200 max-h-48 overflow-y-auto">
                {filteredParks.slice(0, 6).map(park => (
                  <button
                    key={park.id}
                    onClick={() => selectPark(park)}
                    className={`w-full px-4 py-3 text-left border-b border-slate-100 last:border-0 flex items-center justify-between ${
                      formData.park_id === park.id ? 'bg-[#1F7A78]/10' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div>
                      <div className="font-medium text-slate-800">{park.name}</div>
                      {park.postcode && (
                        <div className="text-xs text-slate-500">{park.postcode}</div>
                      )}
                    </div>
                    {formData.park_id === park.id && (
                      <CheckCircle2 className="h-5 w-5 text-[#1F7A78]" />
                    )}
                  </button>
                ))}
              </div>
            )}
            
            {/* Unit Input */}
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Unit / Plot</label>
              <Input
                type="text"
                placeholder="e.g. Plot 42"
                value={formData.unit_name}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_name: e.target.value }))}
                className="h-12 text-base rounded-xl"
                data-testid="quick-unit-input"
              />
            </div>
            
            {/* Navigation */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 h-12 rounded-xl"
              >
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!formData.park_id}
                className="flex-1 h-12 bg-[#1F7A78] hover:bg-[#005f5f] rounded-xl"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Step 3: Details & Confirm */}
        {step === 3 && (
          <div className="space-y-4" data-testid="step-3-details">
            <h2 className="text-lg font-semibold text-slate-800">Add Details (Optional)</h2>
            
            {/* Summary Card */}
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                {(() => {
                  const issue = QUICK_ISSUES.find(i => i.value === formData.issue_type);
                  const Icon = issue?.icon || HelpCircle;
                  return <Icon className="h-8 w-8 text-[#1F7A78]" />;
                })()}
                <div>
                  <div className="font-medium text-slate-800">
                    {QUICK_ISSUES.find(i => i.value === formData.issue_type)?.label}
                  </div>
                  <div className="text-sm text-slate-500">
                    {formData.park_name}{formData.unit_name ? ` • ${formData.unit_name}` : ''}
                  </div>
                </div>
                <Badge className={PRIORITY_OPTIONS.find(p => p.value === formData.priority)?.color || ''}>
                  {formData.priority?.toUpperCase()}
                </Badge>
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Notes</label>
              <textarea
                placeholder="Add any details..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-base resize-none"
                data-testid="quick-description"
              />
            </div>
            
            {/* Photo Capture */}
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Photos</label>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:border-[#1F7A78] hover:text-[#1F7A78] transition-colors"
                >
                  <Camera className="h-6 w-6" />
                  <span className="text-xs mt-1">Add</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  className="hidden"
                  onChange={handlePhotoCapture}
                  data-testid="quick-photo-input"
                />
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative w-20 h-20">
                    <img 
                      src={photo.preview} 
                      alt={`Photo ${idx + 1}`}
                      className="w-full h-full object-cover rounded-xl"
                    />
                    <button
                      onClick={() => removePhoto(idx)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Contact */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1">Contact</label>
                <Input
                  type="text"
                  placeholder="Name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1">Phone</label>
                <Input
                  type="tel"
                  placeholder="Phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
            </div>
            
            {/* Navigation */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setStep(2)}
                variant="outline"
                className="flex-1 h-12 rounded-xl"
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={creating}
                className="flex-1 h-14 bg-[#1F7A78] hover:bg-[#005f5f] rounded-xl text-base font-semibold"
                data-testid="quick-submit-btn"
              >
                {creating ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-5 w-5 mr-2" />
                    Create Job
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
}
